import React, {
  useCallback,
  useEffect,
} from 'react'
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux'
import { Redirect } from 'react-router-dom'

import { InsuranceType } from 'types'

import isDebug from 'utils/isDebug'

import { RootState } from 'state/types'

import { AvailableInsuranceType } from 'state/quotes/types'
import { submitIncrementalQuoteRequest } from 'state/quotes/actions'
import {
  getCurrentSelectedInsuranceTypeSlug,
  getQuoteRequestValue,
} from 'state/quotes/selectors'

import { cacheCoverageLevels } from 'state/coverageLevels/actions'
import {
  cacheAllAccidentDescriptionOptions,
  cacheAllAutoPriorCarriers,
  cacheAllConstructionTypes,
  cacheAllFoundationTypes,
  cacheAllGenders,
  cacheAllHeatingTypes,
  cacheAllHomePriorCarriers,
  cacheAllHomeYearsAtAddressTypes,
  cacheAllHydrantDistanceTypes,
  cacheAllIndustries,
  cacheAllMaritalStatuses,
  cacheAllReasonsForLapse,
  cacheAllAutoReasonsForNoPriorInsurance,
  cacheAllRelations,
  cacheAllRoofTypes,
} from 'state/attributes/actions'
import { cacheAllVehicleYears } from 'state/vehicleAttributes/actions'

import {
  events,
  useGoogleTagManager,
} from 'hooks/useGoogleTagManager'

import QuotingEmail from './index.presentational'

const QuotingEmailContainer: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch()

  const { pushDataLayerEvent } = useGoogleTagManager()

  const email = useSelector((state: RootState) => getQuoteRequestValue(state, 'applicant.address.email'), shallowEqual)

  const [
    applicantFirstName,
    applicantStateAbbr,
    availableInsuranceTypes,
    selectedInsuranceTypeSlug,
    selectedInsuranceTypes,
  ] = useSelector((state: RootState) => ([
    getQuoteRequestValue(state, 'applicant.personalInfo.name.firstName'),
    getQuoteRequestValue(state, 'applicant.address.stateAbbr'),
    (getQuoteRequestValue(state, 'availableInsuranceTypes') || []) as AvailableInsuranceType[],
    getCurrentSelectedInsuranceTypeSlug(state),
    getQuoteRequestValue(state, 'selectedInsuranceTypes'),
  ]), shallowEqual)

  // Cache everything for the selected insurance type(s)
  useEffect(() => {
    const fetches = [
      ...((selectedInsuranceTypes.auto && [
        cacheAllAccidentDescriptionOptions,
        cacheAllAutoPriorCarriers,
        cacheAllAutoReasonsForNoPriorInsurance,
        cacheAllGenders,
        cacheAllMaritalStatuses,
        cacheAllReasonsForLapse,
        cacheAllRelations,
        cacheAllVehicleYears,
        cacheCoverageLevels(InsuranceType.auto, applicantStateAbbr),
      ]) || []),
      ...((selectedInsuranceTypes.home && [
        cacheAllConstructionTypes,
        cacheAllFoundationTypes,
        cacheAllGenders,
        cacheAllHeatingTypes,
        cacheAllHomePriorCarriers,
        cacheAllHydrantDistanceTypes,
        cacheAllHomeYearsAtAddressTypes,
        cacheAllIndustries,
        cacheAllRoofTypes,
        cacheCoverageLevels(InsuranceType.home, applicantStateAbbr),
      ]) || []),
    ]

    Promise.all(fetches.map(fetch => dispatch(fetch()))).catch(e => {
      if (isDebug) {
        console.warn('[DEBUG] An error occurred trying to cache data. Some features may not work properly.', e) // eslint-disable-line no-console
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Only supporting one insurance type at a time in the flow as of https://bitbucket.org/flowerpress/insurancelounge.com/issues/633/update-insurance-types-selection-widget
  const insuranceType = availableInsuranceTypes.find((availableInsuranceType: { slug: string }) => availableInsuranceType.slug === selectedInsuranceTypeSlug)

  const onSubmit = useCallback((): void => {
    if (!insuranceType) {
      return
    }

    dispatch(submitIncrementalQuoteRequest(insuranceType.slug))

    pushDataLayerEvent({
      ...events.email_submitted,
      email_address: email,
      product_type: insuranceType.slug as keyof typeof InsuranceType,
    })
  }, [
    dispatch,
    email,
    insuranceType,
    pushDataLayerEvent,
  ])

  if (!Object.values(selectedInsuranceTypes).filter(value => value).length) {
    return React.createElement(Redirect, { to: '/quote/new/insurance-types' })
  }

  const nextUrl =
    (selectedInsuranceTypeSlug &&
      insuranceType &&
      insuranceType.isAvailableOnline,
    `/quote/new/${selectedInsuranceTypeSlug}`) ||
    '/404'

  return React.createElement(
    QuotingEmail,
    {
      applicantFirstName,
      insuranceType,
      nextUrl,
      onSubmit,
    },
  )
}

export default QuotingEmailContainer
